import { useCallback, useEffect, useState } from 'react';

export interface BoletoMessage {
  type: 'success' | 'warning' | 'error';
  text: string;
  /**
   * E3 (12/10/2026, aprovado pelo Heitor): a mensagem FICA até a pessoa fechar. Vale para o erro de ação que não se
   * desfaz (baixa, emissão, retorno) e para a falha parcial: sumir em 5 s deixava o operador sem saber o que não foi.
   */
  fixa?: boolean;
}

/** O tom da faixa no mapa de tons: o erro é danger; sucesso e warning têm o próprio. */
export const tomDaMensagem = (m: BoletoMessage): 'success' | 'warning' | 'danger' => (m.type === 'error' ? 'danger' : m.type);

/**
 * Ações da tela de Boletos.
 *
 * Substitui as sete flags booleanas de "rodando" por um único `busy` (o nome da
 * ação em curso) e absorve o try/catch/setMessage/reload que estava clonado em
 * quatro handlers idênticos. Duas ações nunca podiam rodar juntas de qualquer
 * forma — as flags separadas só permitiam estados impossíveis.
 */
export function useBoletoActions(reload: () => void | Promise<void>) {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<BoletoMessage | null>(null);

  useEffect(() => {
    if (!message || message.fixa) return;
    const timer = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  const runAction = useCallback(
    async <T,>(
      kind: string,
      fn: () => Promise<T>,
      opts?: {
        /** O texto de sucesso, ou a mensagem inteira (a falha parcial é warning fixa). */
        success?: (result: T) => string | BoletoMessage;
        error?: string;
        /** E3: ação que não se desfaz; o erro dela não some sozinho. */
        irreversivel?: boolean;
        reloadAfter?: boolean;
        onSuccess?: (result: T) => void;
      },
    ): Promise<T | null> => {
      setBusy(kind);
      try {
        const result = await fn();
        if (opts?.success) {
          const m = opts.success(result);
          setMessage(typeof m === 'string' ? { type: 'success', text: m } : m);
        }
        opts?.onSuccess?.(result);
        if (opts?.reloadAfter !== false) await reload();
        return result;
      } catch (err: any) {
        setMessage({
          type: 'error',
          text: err?.response?.data?.message || opts?.error || 'Erro ao executar a ação.',
          fixa: !!opts?.irreversivel,
        });
        return null;
      } finally {
        setBusy(null);
      }
    },
    [reload],
  );

  return { busy, isBusy: (kind: string) => busy === kind, message, setMessage, runAction };
}
